import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Alert, Upload, Typography } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { addNote, uploadNote } from "./api/api";
import FormModal from "../../../components/FormModal";

const { Dragger } = Upload;

const formatErrorMessage = (error, defaultMsg) => {
  try {
    let errorData = error.response?.data;
    let msgStr = "";
    const topLevelError =
      errorData && errorData.error ? errorData.error : defaultMsg;

    if (errorData && typeof errorData === "object") {
      msgStr = errorData.exception || errorData.message || errorData.error;
    } else if (typeof errorData === "string") {
      msgStr = errorData;
    } else {
      msgStr = error.message;
    }

    if (msgStr && typeof msgStr === "string") {
      const jsonMatch = msgStr.match(/\{.*\}/);
      if (jsonMatch) {
        try {
          const cleanJsonStr = jsonMatch[0].replace(/\\"/g, '"');
          const parsed = JSON.parse(cleanJsonStr);
          const innerMsg = parsed.message || "";
          if (innerMsg === "-1" || innerMsg.trim() === "") {
            msgStr = parsed.exception
              ? `${topLevelError} (${parsed.exception})`
              : topLevelError;
          } else {
            msgStr = innerMsg || parsed.exception || msgStr;
          }
        } catch (e) {}
      }
      const splitMsg = msgStr.split(": ");
      if (splitMsg.length > 1 && msgStr.includes("Exception")) {
        msgStr = splitMsg[splitMsg.length - 1];
      }
      return msgStr.replace(/\\u0027/g, "'");
    }
  } catch (e) {
    console.error("Failed to format error:", e);
  }
  return defaultMsg;
};

export function AddNoteModal({ open, onClose, onSuccess, messageApi }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const response = await addNote({ path: values.path });
      if (response.status === 200 || response.status === 201) {
        messageApi.success("Note created successfully");
        form.resetFields();
        onSuccess();
        onClose();
      }
    } catch (error) {
      messageApi.error(formatErrorMessage(error, "Failed to create note"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New Note"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="path"
        label="Path"
        rules={[{ required: true, message: "Please input the path!" }]}
      >
        <Input placeholder="Enter note path" />
      </Form.Item>
      <Alert
        message="Use '/' to create folders. Example: /NoteDirA/Note1"
        type="info"
        showIcon
        style={{ marginTop: "16px" }}
      />
    </FormModal>
  );
}

export function UploadNoteModal({ open, onClose, onSuccess, messageApi }) {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (fileList.length === 0) return;
    const fileToUpload = fileList[0];

    if (!fileToUpload.name.toLowerCase().endsWith(".json")) {
      messageApi.error("Only JSON files are allowed");
      return;
    }

    setUploading(true);
    try {
      await uploadNote(fileToUpload);
      messageApi.success("Note uploaded successfully");
      setFileList([]);
      onSuccess();
      onClose();
    } catch (error) {
      messageApi.error(formatErrorMessage(error, "Failed to import note"));
      //throw error;
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      if (file.size > 10 * 1024 * 1024) {
        messageApi.error("File size exceeds the 10 MB limit");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
    accept: ".json",
  };

  return (
    <Modal
      title="Upload Note JSON"
      open={open}
      onCancel={() => {
        setFileList([]);
        onClose();
      }}
      onOk={handleUpload}
      okText="Upload"
      cancelText="Cancel"
      confirmLoading={uploading}
      okButtonProps={{ disabled: fileList.length === 0 }}
    >
      <Typography.Paragraph>Select JSON file to upload:</Typography.Paragraph>
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload. Only accepts `.json` files
          (max 10 MB)
        </p>
      </Dragger>
    </Modal>
  );
}
